/**
 * @format
 */
import { inject, Injectable } from '@angular/core';
import {
    GenericPlanEducationOption,
    PlanStudyRightService,
    PlanValidationResult,
    PlanValidationTs,
    ValidatablePlan,
} from 'common-typescript';
import {
    Attainment,
    AttainmentType,
    CourseUnit,
    CourseUnitAttainment,
    CustomCourseUnitAttainment,
    CustomModuleAttainment,
    Education,
    ModuleAttainment,
    OtmId,
    Plan,
    StudyRight,
    UniversitySettings,
} from 'common-typescript/types';
import * as _ from 'lodash-es';
import { combineLatest, distinctUntilChanged, forkJoin, from, map, Observable, of, shareReplay, switchMap, take } from 'rxjs';
import {
    AppErrorHandler,
    COMMON_PLAN_SERVICE,
    convertAJSPromiseToNative,
    findMatchingStudyRight,
    firstStudyRightForPlanIgnoringDocumentState,
    GradeScaleEntityService,
    hasStudyRightForPlanButNoneActive,
    PlanEntityService,
    PlanStateAndData,
    PlanStateService,
    RawPlanEditService,
    sisuChain,
    StudyRightEntityService,
    studyRightsForPlan,
    UniversityService,
    VALID_ATTAINMENT_FILTER_SERVICE,
} from 'sis-components';
import { AttainmentStudentService } from '../common/service/attainment-student.service';
import { StudentPlanOutdatedCourseUnitsService } from '../common/service/student-plan-outdated-course-units.service';

export interface PlanStructureData {
    allStudentPlans: Plan[];
    plan: Plan;
    allStudentStudyRights: StudyRight[];
    planStateAndData: PlanStateAndData;
    validatablePlan: ValidatablePlan;
    validatablePlanEducation: Education;
    planValidationResult: PlanValidationResult;
    matchingStudyRight: StudyRight;
    studyRightStatus: PlanStudyRightStatus;
    universitySettings: UniversitySettings;
    educationOptions: GenericPlanEducationOption[];
    outDatedCourseUnitsInPlan: [CourseUnit, CourseUnit][];
    hasOptionsRequiringConfirmation: boolean;
    unplannedAttainments: UnplannedAttainments;
}

/** What the plan page tells about the study rights of the plan. Each field follows its own rule from plan-study-right.ts. */
export interface PlanStudyRightStatus {
    hasNoStudyRight: boolean;
    hasStudyRightButNoneActive: boolean;
    /** The study right whose term registration the page checks: the first one for the plan, in any state */
    termRegistrationStudyRight: StudyRight | undefined;
    /** The study right whose messages with the tutor the page offers: the first one for the plan, even a deleted one */
    tutoringStudyRight: StudyRight | undefined;
}

export interface UnplannedAttainments {
    courseUnitAttainments: CourseUnitAttainment[];
    customCourseUnitAttainments: CustomCourseUnitAttainment[];
    moduleAttainments: ModuleAttainment[];
    customModuleAttainments: CustomModuleAttainment[];
}

@Injectable({
    providedIn: 'root',
})
export class PlanDataService {
    planEntityService = inject(PlanEntityService);
    appErrorHandler = inject(AppErrorHandler);
    studyRightEntityService = inject(StudyRightEntityService);
    planStateService = inject(PlanStateService);
    gradeScaleEntityService = inject(GradeScaleEntityService);
    universityService = inject(UniversityService);
    commonPlanService = inject(COMMON_PLAN_SERVICE);
    studentPlanOutdatedCourseUnitsService = inject(StudentPlanOutdatedCourseUnitsService);
    attainmentStudentService = inject(AttainmentStudentService);
    validAttainmentFilterService = inject(VALID_ATTAINMENT_FILTER_SERVICE);
    rawPlanEditService = inject(RawPlanEditService);

    createInputDataObservableFromPlanIdInput(planIdInput$: Observable<OtmId>): Observable<[string, Plan[], StudyRight[]]> {
        return this.createInputDataObservable(planIdInput$, this.createGetMyPlansObservable(), this.createStudyRightsObservable());
    }

    createInputDataObservable(
        planIdInput$: Observable<OtmId>,
        allStudentPlans$: Observable<Plan[]>,
        studyRights$: Observable<StudyRight[]>,
    ): Observable<[string, Plan[], StudyRight[]]> {
        return combineLatest([planIdInput$, allStudentPlans$, studyRights$]).pipe(
            distinctUntilChanged((current, previous) => {
                const [curPlanId, curPlans, curStudyRights] = current;
                const [prevPlanId, prevPlans, prevStudyRights] = previous;
                return curPlanId === prevPlanId && _.isEqual(curPlans, prevPlans) && _.isEqual(curStudyRights, prevStudyRights);
            }),
            shareReplay({ bufferSize: 1, refCount: true }),
        );
    }

    resolveNotConfirmed(educationOptions: GenericPlanEducationOption[]): boolean {
        return educationOptions?.some(educationOption => educationOption.isInPlan && educationOption.studyRightState === 'NOT_CONFIRMED');
    }

    createDataObservable(inputData$: Observable<[string, Plan[], StudyRight[]]>): Observable<PlanStructureData> {
        const universitySettings$ = this.createUniversitySettingsObservable();
        return inputData$.pipe(
            map(data => this.selectPlanAndItsStudyRights(data)),
            switchMap(data =>
                forkJoin({
                    validatablePlan: this.createValidatablePlanObservable(data.plan),
                    universitySettings: universitySettings$,
                }).pipe(map(result => ({ ...data, ...result }))),
            ),
            switchMap(data => {
                const { validatablePlan, matchingStudyRight } = data;
                const validatablePlanEducation = this.getValidatablePlanEducation(validatablePlan);
                const educationOptions = this.getEducationOptions(validatablePlan, validatablePlanEducation, matchingStudyRight);
                const planValidationResult = this.getPlanValidationResult(validatablePlan);
                return forkJoin({
                    validatablePlanEducation: of(validatablePlanEducation),
                    planValidationResult: of(planValidationResult),
                    educationOptions: of(educationOptions),
                    gradeScalesById: this.createGradeScalesByIdObservable(validatablePlan),
                    outDatedCourseUnitsInPlan: this.createOutDatedCourseUnitsInPlanObservable(validatablePlan),
                    unplannedAttainments: this.createUnplannedAttainmentsObservable(
                        validatablePlan,
                        data.allStudentPlans,
                        data.matchingStudyRight,
                    ),
                }).pipe(map(result => ({ ...data, ...result })));
            }),
            map(data => {
                const planStateAndData = this.planStateService.getPlanStateAndData({
                    education: data.validatablePlanEducation,
                    validatablePlan: data.validatablePlan,
                    planValidationResult: data.planValidationResult,
                    educationOptions: data.educationOptions,
                    gradeScalesById: data.gradeScalesById,
                    studyRight: data.matchingStudyRight,
                    outDatedCourseUnitsInPlan: data.outDatedCourseUnitsInPlan,
                });
                return { ...data, planStateAndData, hasOptionsRequiringConfirmation: this.resolveNotConfirmed(data.educationOptions) };
            }),
        );
    }

    selectPlanAndItsStudyRights([planId, allStudentPlans, allStudentStudyRights]: [string, Plan[], StudyRight[]]) {
        const plan = this.findSelectedPlan(allStudentPlans, planId);
        const matchingStudyRight = findMatchingStudyRight(plan, allStudentStudyRights);
        const studyRightsOfPlan = studyRightsForPlan(plan, allStudentStudyRights);
        const studyRightStatus: PlanStudyRightStatus = {
            hasNoStudyRight: studyRightsOfPlan.length === 0,
            hasStudyRightButNoneActive: hasStudyRightForPlanButNoneActive(plan, allStudentStudyRights),
            termRegistrationStudyRight: studyRightsOfPlan[0],
            tutoringStudyRight: firstStudyRightForPlanIgnoringDocumentState(plan, allStudentStudyRights),
        };
        return { plan, allStudentPlans, allStudentStudyRights, matchingStudyRight, studyRightStatus };
    }

    createGetMyPlansObservable(): Observable<Plan[]> {
        return this.planEntityService
            .getMyPlans()
            .pipe(this.appErrorHandler.defaultErrorHandler(), shareReplay({ bufferSize: 1, refCount: true }));
    }

    findSelectedPlan(allStudentPlans: Plan[], planId: string): Plan {
        return allStudentPlans.find(plan => plan.id === planId);
    }

    createValidatablePlanObservable(plan: Plan): Observable<ValidatablePlan> {
        return from(convertAJSPromiseToNative<ValidatablePlan>(this.commonPlanService.getValidatablePlan(_.cloneDeep(plan), false, false)));
    }

    getPlanValidationResult(validatablePlan: ValidatablePlan): PlanValidationResult {
        return PlanValidationTs.validatePlan(validatablePlan);
    }

    createStudyRightsObservable(): Observable<StudyRight[]> {
        return this.studyRightEntityService
            .getStudyRightsForCurrentUser()
            .pipe(this.appErrorHandler.defaultErrorHandler(), shareReplay({ bufferSize: 1, refCount: true }));
    }

    getValidatablePlanEducation(validatablePlan: ValidatablePlan): Education {
        return validatablePlan.rootModule;
    }

    getEducationOptions(validatablePlan: ValidatablePlan, education: Education, studyRight: StudyRight): GenericPlanEducationOption[] {
        return PlanStudyRightService.getValidatedEducationOptions(validatablePlan, education, studyRight);
    }

    createGradeScalesByIdObservable(validatablePlan: ValidatablePlan) {
        return of(validatablePlan).pipe(
            map(vp => sisuChain(_.values(vp.getAllAttainments())).map('gradeScaleId').concat('sis-0-5').compact().uniq().value()),
            switchMap(gradeScaleIds =>
                this.gradeScaleEntityService.getByIds(gradeScaleIds).pipe(take(1), this.appErrorHandler.defaultErrorHandler()),
            ),
            map(gradeScales => _.keyBy(gradeScales, 'id')),
        );
    }

    createUniversitySettingsObservable(): Observable<UniversitySettings> {
        return this.universityService
            .getCurrentUniversitySettings()
            .pipe(take(1), shareReplay({ bufferSize: 1, refCount: true }), this.appErrorHandler.defaultErrorHandler());
    }

    createOutDatedCourseUnitsInPlanObservable(validatablePlan: ValidatablePlan): Observable<[CourseUnit, CourseUnit][]> {
        return this.studentPlanOutdatedCourseUnitsService
            .checkLatestCourseUnitsAreInSelectedPlan(validatablePlan)
            .pipe(take(1), this.appErrorHandler.defaultErrorHandler());
    }

    createUnplannedAttainmentsObservable(
        validatablePlan: ValidatablePlan,
        includedPlans: Plan[],
        matchingStudyRight: StudyRight,
    ): Observable<UnplannedAttainments> {
        return this.attainmentStudentService.getMyValidAttainments().pipe(
            take(1),
            map(allAttainments => {
                const unattachedAttainments = allAttainments.filter(
                    attainment => !this.validAttainmentFilterService.isAttached(attainment, allAttainments),
                );
                return {
                    courseUnitAttainments: this.filterCourseUnitAttainments(unattachedAttainments, validatablePlan),
                    customCourseUnitAttainments: this.filterCustomCourseUnitAttainments(
                        unattachedAttainments,
                        validatablePlan,
                        includedPlans,
                        matchingStudyRight,
                    ),
                    moduleAttainments: this.filterModuleAttainments(unattachedAttainments, validatablePlan),
                    customModuleAttainments: this.filterCustomModuleAttainments(unattachedAttainments, validatablePlan, matchingStudyRight),
                };
            }),
            this.appErrorHandler.defaultErrorHandler(),
        );
    }

    private filterCourseUnitAttainments(attainments: Attainment[], validatablePlan: ValidatablePlan): CourseUnitAttainment[] {
        // getMyValidAttainments() dedupes by attainment id, not by courseUnitId, so a course unit that was attained more than
        // once (e.g. a re-attainment raising the grade) can otherwise appear here twice. Keep only the latest one per course unit.
        const latestAttainmentByCourseUnitId = _.uniqBy(
            _.orderBy(this.getCourseUnitAttainments(attainments), 'attainmentDate', 'desc'),
            'courseUnitId',
        );
        return latestAttainmentByCourseUnitId.filter(attainment => {
            const attainedCourseUnit = attainment ? validatablePlan.getCourseUnit(attainment.courseUnitId) : null;
            if (attainedCourseUnit) {
                // Unlike custom attainments, a regular course unit attainment is not bound to a single study right or plan: the
                // same real-world completed course unit can legitimately count towards more than one of the student's plans, so
                // being selected in another primary plan does not disqualify it here (matches RawPlanEditService.
                // getSelectableAttainedCourseUnits, the equivalent legacy free-edit lookup).
                return !validatablePlan.isCourseUnitInPlan(attainedCourseUnit) && !validatablePlan.isSubstitute(attainedCourseUnit);
            }
            return false;
        });
    }

    private filterCustomCourseUnitAttainments(
        attainments: Attainment[],
        validatablePlan: ValidatablePlan,
        includedPlans: Plan[],
        matchingStudyRight: StudyRight,
    ): CustomCourseUnitAttainment[] {
        return this.getCustomCourseUnitAttainments(attainments, validatablePlan).filter(attainment => {
            if (attainment) {
                return !(
                    validatablePlan.isCustomCourseUnitAttainmentInPlan(attainment) ||
                    this.getCustomCourseUnitAttainmentIdsInOtherPrimaryPlans(includedPlans, validatablePlan).includes(attainment.id) ||
                    // Consider attainments without studyRightId as not bound to any study right and include them
                    (attainment.studyRightId && attainment.studyRightId !== matchingStudyRight?.id)
                );
            }
            return false;
        });
    }

    private filterModuleAttainments(attainments: Attainment[], validatablePlan: ValidatablePlan): ModuleAttainment[] {
        return attainments
            .filter(
                (attainment: Attainment) =>
                    attainment.type === AttainmentType.MODULE_ATTAINMENT || attainment.type === AttainmentType.DEGREE_PROGRAMME_ATTAINMENT,
            )
            .map((attainment: Attainment) => attainment as ModuleAttainment)
            .filter(
                (moduleAttainment: ModuleAttainment) =>
                    !this.rawPlanEditService.isModuleInPlanByGroupId(moduleAttainment.moduleGroupId, validatablePlan),
            );
    }

    private filterCustomModuleAttainments(
        attainments: Attainment[],
        validatablePlan: ValidatablePlan,
        matchingStudyRight: StudyRight,
    ): CustomModuleAttainment[] {
        return attainments
            .filter(
                (attainment: Attainment) =>
                    attainment.type === AttainmentType.CUSTOM_MODULE_ATTAINMENT &&
                    !validatablePlan.isModuleAttainmentInPlan(attainment.id) &&
                    (!attainment.studyRightId || attainment.studyRightId === matchingStudyRight?.id),
            )
            .map((attainment: Attainment) => attainment as CustomModuleAttainment);
    }

    private getCourseUnitAttainments(attainments: Attainment[]): CourseUnitAttainment[] {
        return attainments
            .filter(attainment => attainment.type === 'CourseUnitAttainment')
            .map(attainment => attainment as unknown as CourseUnitAttainment);
    }

    private getCustomCourseUnitAttainments(attainments: Attainment[], validatablePlan: ValidatablePlan): CustomCourseUnitAttainment[] {
        return attainments
            .filter(attainment => attainment.type === 'CustomCourseUnitAttainment')
            .map((attainment: CustomCourseUnitAttainment) => validatablePlan.getCustomCourseUnitAttainment(attainment.id));
    }

    private getCustomCourseUnitAttainmentIdsInOtherPrimaryPlans(includedPlans: Plan[], validatablePlan: ValidatablePlan): OtmId[] {
        return includedPlans
            .filter(plan => plan.primary && plan.rootId !== validatablePlan.rootModule.id)
            .flatMap(plan => plan.customCourseUnitAttainmentSelections)
            .map(selection => selection.customCourseUnitAttainmentId);
    }
}
